import { formatProductName } from './utils/product.js';
import logger from './utils/logger.js';
import { g as getItemVariantsWithoutSize } from './helpers-kA_hdoOI.js';
import { getCookie } from './utils/cookies.js';
import { r as COOKIE_ACCOR_GTM_CART_TRACKING } from './cookies-l-jEMp6P.js';
import './currency-_kacFJHO.js';
import './language-BHHwgtQp.js';
import './payment-YC9H491Y.js';

const sendCartEventAccor = ({ cart, isViewCartEvent = false, enableGoogleAds = false, // If we enabled this, we need to be careful to make sure we're not sending PII to Google Ads without consent. This has not been tested.
siteFilters, affiliation, brand, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    window.dataLayer = window.dataLayer || [];
    const products = cart.item_selected || cart.items;
    let grandTotal = 0;
    const currencyCode = (_b = (_a = cart === null || cart === void 0 ? void 0 : cart.prices) === null || _a === void 0 ? void 0 : _a.subtotal_including_tax) === null || _b === void 0 ? void 0 : _b.currency;
    if ((!products || products.length === 0) && !isViewCartEvent) {
        logger('Items not found; "add_to_cart" event not sent', {
            type: 'error',
            category: 'analytics',
        });
        return;
    }
    const isRemovalEvent = products.reduce((acc, curr) => acc + curr.just_removed_qty, 0) >
        0;
    const eventName = (() => {
        if (isViewCartEvent)
            return 'view_cart';
        if (isRemovalEvent)
            return 'remove_from_cart';
        return 'add_to_cart';
    })();
    // Google Ads
    const cartProductsAW = [];
    // GA4
    const cartProductsGA4 = [];
    const cartGtmDataCookie = (_c = getCookie(COOKIE_ACCOR_GTM_CART_TRACKING)) === null || _c === void 0 ? void 0 : _c.toString();
    const cartGtmData = cartGtmDataCookie ? JSON.parse(cartGtmDataCookie) : {};
    for (let i = 0; i < products.length; i += 1) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { product, prices, just_added_qty, just_removed_qty, quantity, configured_variant } = products[i];
        const categoryName = ((_d = [...product.name_en.categories].reverse()[0]) === null || _d === void 0 ? void 0 : _d.name_en) || '';
        const qty = (() => {
            if (isViewCartEvent)
                return quantity;
            if (isRemovalEvent)
                return just_removed_qty;
            return just_added_qty;
        })();
        const discount = (_f = (_e = configured_variant === null || configured_variant === void 0 ? void 0 : configured_variant.price_gap) === null || _e === void 0 ? void 0 : _e.value) !== null && _f !== void 0 ? _f : product.price_gap.value;
        if (qty > 0) {
            grandTotal += prices.price.value * qty;
            if (enableGoogleAds) {
                cartProductsAW.push({
                    id: product.sku,
                    google_business_vertical: 'retail',
                });
            }
            const { extractedVariants, removedSizeVariant } = getItemVariantsWithoutSize(configured_variant, siteFilters);
            const itemListName = cartGtmData[product.sku];
            cartProductsGA4.push({
                affiliation,
                discount,
                index: i,
                item_brand: brand,
                item_category: categoryName,
                ...(removedSizeVariant && {
                    item_category2: removedSizeVariant,
                }),
                item_id: product.sku,
                ...(itemListName && {
                    item_list_name: itemListName,
                }),
                item_name: formatProductName({
                    name: ((_g = product.name_en) === null || _g === void 0 ? void 0 : _g.product_name) || product.name,
                    lang: 'en',
                    siteFilters: siteFilters || null,
                }),
                ...(extractedVariants && {
                    item_variant: extractedVariants,
                }),
                price: prices.price.value,
                quantity: qty,
                subscription: 'no',
            });
        }
    }
    grandTotal = Number(grandTotal.toFixed(2));
    // GA4 add_to_cart, remove_from_cart or view_cart event
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event: eventName,
        ...(enableGoogleAds && {
            event_value: grandTotal,
            event_items: cartProductsAW,
        }),
        ecommerce: {
            items: cartProductsGA4,
            value: grandTotal,
            currency: currencyCode || 'EUR',
        },
    });
    logger(`${eventName} event added`, { category: 'analytics' });
};
const dataLayerCartAccor = ({ items, grandTotal, currencyCode, siteFilters = null, }) => {
    const cartViewProducts = [];
    if (items && items.length > 0) {
        window.dataLayer = window.dataLayer || [];
        for (let i = 0; i < items.length; i += 1) {
            const { product, prices, quantity } = items[i];
            cartViewProducts.push({
                name: formatProductName({
                    name: product.name,
                    lang: 'en',
                    siteFilters: siteFilters || null,
                }),
                sku: product.sku,
                price: prices.price.value,
                quantity: quantity || 1,
                // category: '' // Disabled: unable to accurately get categories from Magento
            });
        }
        // Update cartView DataLayer
        // eslint-disable-next-line no-param-reassign
        window.dataLayer.cartView = {
            products: cartViewProducts,
            totalCost: grandTotal,
            currencyCode,
        };
        logger('Cart view updated', { category: 'analytics' });
    }
    else {
        logger('Items not found; cart view not updated', { type: 'error', category: 'analytics' });
    }
};
const getEventItems = (cartItems, brand, siteFilters, itemListName) => {
    const eventItems = [];
    cartItems.forEach((item, i) => {
        var _a, _b, _c, _d, _e;
        const discount = (_c = (_b = (_a = item.configured_variant) === null || _a === void 0 ? void 0 : _a.price_gap) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : item.product.price_gap.value;
        const categoryName = ((_d = [...item.product.name_en.categories].reverse()[0]) === null || _d === void 0 ? void 0 : _d.name_en) || '';
        const { extractedVariants, removedSizeVariant } = getItemVariantsWithoutSize(item.configured_variant, siteFilters);
        const eventItem = {
            discount,
            index: i,
            item_brand: brand,
            item_category: categoryName,
            ...(removedSizeVariant && {
                item_category2: removedSizeVariant,
            }),
            item_id: item.product.sku,
            ...(itemListName && {
                item_list_name: itemListName,
            }),
            item_name: formatProductName({
                name: ((_e = item.product.name_en) === null || _e === void 0 ? void 0 : _e.product_name) || item.product.name,
                lang: 'en',
                siteFilters: siteFilters || null,
            }),
            ...(extractedVariants && {
                item_variant: extractedVariants,
            }),
            price: item.prices.price.value,
        };
        eventItems.push(eventItem);
    });
    return eventItems;
};
const sendViewItemListEventAccor = ({ brand, cart, itemListName, siteFilters, }) => {
    var _a, _b;
    const currencyCode = (_b = (_a = cart === null || cart === void 0 ? void 0 : cart.prices) === null || _a === void 0 ? void 0 : _a.subtotal_including_tax) === null || _b === void 0 ? void 0 : _b.currency;
    const viewItemListEvent = {
        event: 'view_item_list',
        event_value: null,
        ecommerce: {
            currency: currencyCode || 'EUR',
            item_list_name: itemListName,
            items: getEventItems(cart.items, brand, siteFilters, itemListName),
        },
    };
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(viewItemListEvent);
};

export { sendViewItemListEventAccor as a, dataLayerCartAccor as d, sendCartEventAccor as s };
//# sourceMappingURL=cart-SVV9q5Ln.js.map
